import { Environments } from "../constants/environments";
import { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { AtticusLogger } from "./AtticusLogger";

/**
 * Network enables simple REST calls via axios
 *  errors can be configured to throw or be handled via reportError function
 *  some helper methods are also included
 */
export class Network {
  constructor(
    private axiosInstance: AxiosInstance,
    private atticusLogger: AtticusLogger,
    private authPath: string,
    private maintenancePath?: string
  ) {}

  // environment helpers
  static get isProd(): boolean {
    return process.env.NODE_ENV === Environments.prod;
  }
  static get isDev(): boolean {
    return process.env.NODE_ENV === Environments.dev;
  }
  static get isTest(): boolean {
    return process.env.NODE_ENV === Environments.test;
  }

  async callGet(
    path: string,
    rethrowError = false,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse | null> {
    try {
      return await this.axiosInstance.get(path, config);
    } catch (error) {
      if (rethrowError) {
        throw error;
      } else {
        this.handleError(error);
      }
    }
    return null;
  }

  async callPost(
    path: string,
    body?: Record<string, unknown>,
    rethrowError = false,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse | null> {
    try {
      return await this.axiosInstance.post(path, body, config);
    } catch (error) {
      if (rethrowError) {
        throw error;
      } else {
        this.handleError(error);
      }
    }
    return null;
  }

  async callPut(
    path: string,
    body?: Record<string, unknown>,
    rethrowError = false,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse | null> {
    try {
      return await this.axiosInstance.put(path, body, config);
    } catch (error) {
      if (rethrowError) {
        throw error;
      } else {
        this.handleError(error);
      }
    }
    return null;
  }

  async callDelete(
    path: string,
    rethrowError = false
  ): Promise<AxiosResponse | null> {
    try {
      return await this.axiosInstance.delete(path);
    } catch (error) {
      if (rethrowError) {
        throw error;
      } else {
        this.handleError(error);
      }
    }
    return null;
  }

  handleError(error: any): void {
    if (error?.response?.status === 401) {
      window.document.location.href = this.authPath;
    } else if (error?.response?.status === 503 && this.maintenancePath) {
      // service unavailable, come back later
      window.document.location.href = this.maintenancePath;
    } else {
      const errorObject = {
        message: error?.message,
        baseURL: error?.config?.baseURL,
        url: error?.config?.url,
        method: error?.config?.method,
      };
      this.atticusLogger.error(errorObject);
    }
  }

  /**
   * artificial delay for testing
   * @param milliseconds
   */
  async addDelay(milliseconds = 500): Promise<void> {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve();
      }, milliseconds);
    });
  }
}
